import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { client, onForbidden, onSessionLost, onTwoFactorHeld, setCsrf } from './api.ts';
import { ACCESS_CLOSED, ACCESS_EVENTS, sameList } from './shared/access.ts';
import { BoardScreen } from './Board.tsx';
import { BookScreen } from './Book.tsx';
import { BookPage } from './book/BookPage.tsx';
import { AgentsScreen } from './agents/Agents.tsx';
import { BooksScreen } from './books/Books.tsx';
import type { AgentsPart } from './agents/props.ts';
import type { BooksPart } from './books/props.ts';
import { CAME_BACK_FROM_LEDGER } from './books/ledger-connect.ts';
import { CallsScreen } from './Calls.tsx';
import { CallsPage } from './calls/CallsPage.tsx';
import { RingingBanner } from './calls/RingingBanner.tsx';
import { CallsStripHost } from './calls/CallsStripHost.tsx';
import { CALL_EVENTS, openAfterRing } from './calls/ringing-model.ts';
import { CompanyScreen } from './account/Company.tsx';
import { YourPlanPage } from './account/YourPlanPage.tsx';
import {
  gettingStartedUntouched,
  lateMove,
  watchTouches,
  type TouchWatch,
} from './account/getting-started.ts';
import { ConnectionLine } from './connection/ConnectionLine.tsx';
import { allowLiveUpdates, onLiveChange } from './connection/live.ts';
import { MODE_EVENTS, setCompanyMode } from './shared/mirror.ts';
import { setTrial } from './shared/trial.ts';
import { mayTurnOnMaps, setMapsKey } from './shared/maps-key.ts';
import { Login } from './Login.tsx';
import { SignInPage } from './SignInPage.tsx';
import { SignInScreen, TwoFactorHold } from './account/SignInScreen.tsx';
import { AiToolsPage } from './account/AiToolsPage.tsx';
import { YourSignInPage } from './account/YourSignInPage.tsx';
import { twoFactorState } from './account/TwoFactorBanner.tsx';
import {
  BOOKER_PERMS,
  canSee,
  inOfficeApp,
  linkedPlace,
  notYoursWords,
  PLACE_LINK,
  PLACES,
  REVIEW_PERMS,
  type NavProps,
  type View,
} from './Nav.tsx';
import { InventoryScreen } from './inventory/Inventory.tsx';
import { JobRoute } from './job/JobRoute.tsx';
import {
  JOB_LINK,
  jobHref,
  leaveJobPage,
  openJob,
  openedInApp,
  parseJobLink,
  visitOpenedFrom,
} from './job/job-page.ts';
import { InvoiceSheetHost, openInvoice } from './invoice/InvoiceSheet.tsx';
import { SellMembershipHost } from './memberships/SellMembership.tsx';
import { today as todayIn } from './book-model.ts';
import { MembershipsScreen } from './memberships/Memberships.tsx';
import { MigrationScreen } from './migration/Migration.tsx';
import { HuddleScreen } from './huddle/Huddle.tsx';
import { ReportsScreen } from './reports/Reports.tsx';
import { ReportsPage } from './reports/ReportsPage.tsx';
import { CustomersScreen } from './history/Customers.tsx';
import { ReviewScreen } from './review/Review.tsx';
import { HoursScreen } from './hours/Hours.tsx';
import { HoursRoute } from './timesheet/HoursRoute.tsx';
import { ListsScreen } from './lists/Lists.tsx';
import { TasksScreen } from './tasks/Tasks.tsx';
import { seesTasks } from './tasks/tasks-model.ts';
import { MessagesScreen, type MessagesStart } from './messages/Messages.tsx';
import { INBOX_EVENTS, useInboxCounts } from './shared/inbox-counts.ts';
import { useNotifications } from './shared/Notifications.tsx';
import { sourceOf, type Note } from './shared/notifications-model.ts';
import { SettingsScreen } from './settings/Settings.tsx';
import { ToBookScreen } from './tobook/ToBook.tsx';
import { ToSchedulePage } from './tobook/ToSchedulePage.tsx';
import type { Lead, SoldEstimate } from './tobook/tobook-model.ts';
import type { BookStart } from './types.ts';
import { seesPayroll } from './books/model.ts';
import { DOC_LINK, parseDocLink, type DocRef } from './shared/doc-link.ts';
import { CUSTOMER_LINK, customerHref } from './history/customer-link.ts';
import { locateDoc } from './shared/open-doc.ts';
import { holdsAny } from './shared/may.ts';
import { DesignContext, designOf, type Design } from './blueprint/design.ts';
import { Frame } from './blueprint/Frame.tsx';
import { HomeScreen } from './blueprint/Home.tsx';
import { AdCampaignsScreen, AdLeadsScreen, AdsScreen } from './blueprint/Ads.tsx';
import { ProfitJobsScreen, ProfitScreen } from './blueprint/Profit.tsx';
import { PerformanceScreen } from './blueprint/Performance.tsx';
import { ownScorecardOnly } from './blueprint/performance-model.ts';
import {
  blueprintLink,
  blueprintOnly,
  canOpenIn,
  linkedPart,
  movedLink,
  opensOffice,
  pageLabel,
  PART_LINK,
} from './blueprint/places.ts';
import type { SettingsPage } from './settings/parts.ts';
import { MapScreen } from './schedule/SchedulePages.tsx';
import { TeamScreen } from './schedule/TeamScreen.tsx';
import { TimeOffScreen } from './schedule/TimeOffScreen.tsx';
import { ApprovalScreen, NotificationsScreen } from './inbox/InboxPages.tsx';
import { EquipmentScreen } from './history/EquipmentPage.tsx';
import { CatalogScreen } from './inventory/Catalog.tsx';
import type { OpenTarget } from '@flowology/ui-web';
import './blueprint/blueprint.css';

type Session = {
  tenant: string;
  companyName?: string;
  roles: string[];
  /** What the person holds, their roles' and the owner's grants (Operations' pages ask, P-0164). */
  permissions: string[];
  staffId: string | null;
  /** The person's membership in this company: what this browser remembers for them (the board's density). */
  who?: string;
};

/**
 * A person's own first place, by the permissions they hold (QA-680): the board for whoever dispatches,
 * the books for whoever keeps them, Calls for the rest of the office, and otherwise the first place they
 * open (the warehouse's Inventory).
 */
function firstPlaceFor(held: readonly string[]): View {
  const opens = (v: View) => PLACES.some((p) => p.view === v && canSee(p, held));
  if (held.includes('jobs.dispatch') && opens('board')) return 'board';
  if (opens('books')) return 'books';
  if (opens('calls')) return 'calls';
  return PLACES.find((p) => canSee(p, held))?.view ?? 'calls';
}

/** Your sign-in's own link (QA-093). */
const YOUR_SIGN_IN = '#your-sign-in';

/** The <style> the company's theme goes in: on the sign-in page (from /brand) and once signed in. */
function themeStyle(): HTMLElement {
  let el = document.getElementById('flow-theme');
  if (!el) {
    el = document.createElement('style');
    el.id = 'flow-theme';
    document.head.prepend(el);
  }
  return el;
}

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [view, setView] = useState<View>('calls');
  // a day picked on Schedule's Week opens on the board (Blueprint)
  const [boardDay, setBoardDay] = useState<{ date: string; at: number; visit?: string } | null>(null);
  const [callId, setCallId] = useState<string | null>(null);
  const [ringing, setRinging] = useState(0);
  // the call ringing while the person is elsewhere (QA-726): the shell's banner, read again on its changes
  const [ringingCall, setRingingCall] = useState<{ id: string; tick: number } | null>(null);
  const ringingRef = useRef(ringingCall);
  ringingRef.current = ringingCall;
  const [bookStart, setBookStart] = useState<BookStart | null>(null);
  // Messages opened from a job sheet: only that job's (QA-391)
  const [messagesStart, setMessagesStart] = useState<MessagesStart | null>(null);
  const [timeZone, setTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  // The session ended under an open screen: sign in again over it, so nothing typed is lost (QA-251).
  const [lost, setLost] = useState(false);
  const [recoveryLeft, setRecoveryLeft] = useState<number | null>(null);
  // Past a required two-factor's deadline: every place but Your sign-in is held (QA-089, QA-093).
  const [blocked, setBlocked] = useState(false);
  const onTwoFactor = useCallback((t: { blocked: boolean }) => setBlocked(t.blocked), []);
  // The deadline passed while a screen was open: a held call says so, and the places are held.
  useEffect(() => {
    onTwoFactorHeld(() => void twoFactorState(true).then((t) => setBlocked(t?.blocked ?? true)));
  }, []);
  useEffect(() => {
    onSessionLost(() => {
      if (session) setLost(true);
    });
  });
  // The notifications place (QA-097): read for anyone in the office, kept current by the stream.
  const notes = useNotifications(!!session && inOfficeApp(session.permissions));
  const reloadNotes = useRef(notes.reload);
  reloadNotes.current = notes.reload;
  // What waits in the Inbox, counted on it and its places (QA-344); the stream keeps it current.
  // Each count by the permission its action asks (QA-680), whichever of the person's roles gives it.
  const has = (perms: readonly string[]) => !!session && holdsAny(session.permissions, perms);
  const inbox = useInboxCounts(
    {
      tobook: has(BOOKER_PERMS),
      review: has(REVIEW_PERMS),
      messages: has(['messages.view']),
      // the Inbox's Calls tab (BL-132): calls.callbacks is the office's, as Calls is (calls.view)
      callbacks: has(['calls.view']),
      // their own tasks due by today (BL-004); someone with no staff record has none
      tasks: !!session && seesTasks(session.permissions) && !!session.staffId,
      // new price book pictures waiting for approval (BL-122): pricebook.edit's
      pictures: has(['pricebook.edit']),
    },
    timeZone,
  );
  const reloadInbox = useRef(inbox.reload);
  reloadInbox.current = inbox.reload;
  // A notification that opens a part of Books (Setup for a posting alert, say), or a document link
  // that opens something in one (a journal entry, a pay period).
  const [booksPart, setBooksPart] = useState<{ part: BooksPart; at: number; focus?: string } | null>(null);
  // A part of Settings asked for by its link (#/settings/people) or a rail page.
  const [settingsPart, setSettingsPart] = useState<{ part: SettingsPage; at: number } | null>(null);
  // The part each screen with parts shows now, as it says (onPart): the address follows it.
  const [shownPart, setShownPart] = useState<{ books?: BooksPart; settings?: SettingsPage }>({});
  /** Open a part of Books or Settings, as its link or a rail page asks. */
  const askPart = (v: View, part: string) => {
    const at = Date.now();
    if (v === 'books') setBooksPart({ part: part as BooksPart, at });
    if (v === 'settings') setSettingsPart({ part: part as SettingsPage, at });
  };
  // A document link's customer (an invoice or payment, on their Money tab) or job at Review (a draft);
  // Blueprint's Equipment opens a customer on one piece of theirs.
  const [customerOpen, setCustomerOpen] = useState<{
    customerId: string;
    focus?: string;
    equipmentId?: string;
    at: number;
  } | null>(null);
  // The board's link to a technician's hours (a missed clock-out): Dispatch › Hours, on them; from
  // their clock line under their name (BL-126), on that day. Blueprint's Books › Payroll opens Hours
  // on a pay period's day, with no one picked.
  const [hoursOpen, setHoursOpen] = useState<{ staffId?: string; date?: string; at: number } | null>(null);
  // The tech menu's Truck stock: Stock at that truck alone (Inventory), until they leave it
  const [stockOpen, setStockOpen] = useState<{ locationId: string; at: number } | null>(null);
  // BL-128: a job's one page (#/jobs/<id>) over the place it was opened from: the board stays mounted
  // under it as it was; any other place draws again when the page closes, as its address says
  const [jobPage, setJobPage] = useState<string | null>(() => parseJobLink(location.hash));
  // Why a document link didn't open (the place isn't theirs, the server said no): said, then gone.
  const [docSaid, setDocSaid] = useState<string | null>(null);
  useEffect(() => {
    if (!docSaid) return;
    const t = setTimeout(() => setDocSaid(null), 6000);
    return () => clearTimeout(t);
  }, [docSaid]);
  // The place a document link opened, so the address keeps the link while it's there ('pending' while
  // it's being looked up); any other place puts the place's own link back.
  const docView = useRef<View | 'pending' | null>(null);
  // Leaving the place a document link opened: coming back later is the place itself, not the document.
  useEffect(() => {
    if (view !== 'customers') setCustomerOpen(null);
    if (view !== 'hours') setHoursOpen(null);
    if (view !== 'inventory') setStockOpen(null);
    if (view !== 'books') setBooksPart((b) => (b?.focus ? { part: b.part, at: b.at } : b));
    // a screen left says no part: coming back, it says again which it opens
    setShownPart((o) => ({
      ...(view === 'books' ? { books: o.books } : {}),
      ...(view === 'settings' ? { settings: o.settings } : {}),
    }));
  }, [view]);
  // …or a part of Agents (Support, for a text that couldn't go).
  const [agentsPart, setAgentsPart] = useState<{ part: AgentsPart; at: number } | null>(null);
  // a membership's renewal notification opens Renewals on its row (BL-009)
  const [membershipsPart, setMembershipsPart] = useState<{
    part: 'renewals';
    at: number;
    focus?: string;
  } | null>(null);
  const viewRef = useRef(view);
  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  // The first place sign-in chose when no link said where to go: Home replaces it for a Blueprint owner.
  const landing = useRef<View | null>(null);
  // A link at sign-in to a place that isn't theirs and has its own words (#/hours, QA-774): said once
  // the design is known, since only Blueprint says it.
  const notYours = useRef<string | null>(null);
  // Whether they've touched the page since sign-in, until the theme answers: Home never moves them once
  // they have (QA-677), as Getting started doesn't (QA-583).
  const landingTouch = useRef<TouchWatch | null>(null);

  // The first place: the board for dispatchers, the books for the bookkeeper, calls for everyone
  // else. Set once, at sign-in.
  /** A part's own link (#/books/banking): its screen, when what they hold opens it, asked to open on the part. */
  const partLink = (hash: string, held: readonly string[]): View | null => {
    const l = linkedPart(hash);
    const v = l ? linkedPlace(`#/${l.view}`, held) : null;
    if (!l || !v) return null;
    askPart(v, l.part);
    return v;
  };

  /** A link to a page that moved to a place of its own (#/ads is #/marketing now, BL-136): its new one. */
  const followMoved = () => {
    const to = movedLink(location.hash);
    if (to) history.replaceState(null, '', `${location.pathname}${location.search}${to}`);
  };

  const begin = (s: Session | null) => {
    if (s) followMoved();
    const first: View | null = !s
      ? null
      : // A Blueprint page's own link (#/home, #/marketing): checked again once the design is known.
        (blueprintLink(location.hash, s.permissions) ??
        // A place's own link (a reload, a link someone sent).
        linkedPlace(location.hash, s.permissions) ??
        // A part's own link (#/books/banking): its screen, open on it.
        partLink(location.hash, s.permissions) ??
        // An open customer's own link (#/customers/<id>): their record, for those who open Customers.
        (CUSTOMER_LINK.test(location.hash) ? linkedPlace('#/customers', s.permissions) : null) ??
        // A job's page (BL-128), reached by a link: over the board, or Customers for those without it.
        (JOB_LINK.test(location.hash)
          ? (linkedPlace('#/board', s.permissions) ?? linkedPlace('#/customers', s.permissions))
          : null) ??
        // A link to Your sign-in (the two-factor banner's, an email's).
        (location.hash === YOUR_SIGN_IN
          ? 'signin'
          : // Back from our billing provider's checkout: the subscription.
            location.hash === '#billing' && s.permissions.includes('billing.manage')
            ? 'company'
            : // Back from connecting QuickBooks or Xero: the books.
              CAME_BACK_FROM_LEDGER
              ? 'books'
              : firstPlaceFor(s.permissions)));
    // Where no link sent them: in Blueprint the owner starts on Home instead (once the design is known).
    landing.current = s && !location.hash && !CAME_BACK_FROM_LEDGER ? first : null;
    notYours.current = s ? notYoursWords(location.hash, s.permissions) : null;
    landingTouch.current?.stop();
    landingTouch.current = s ? watchTouches(document) : null;
    if (first) setView(first);
    setSession(s);
    // A new company's owner lands on Getting started until its basics are done (QA-329), unless a
    // link or a return from billing or the ledger said where to go.
    if (s?.roles.includes('owner') && !location.hash && !CAME_BACK_FROM_LEDGER)
      void gettingStartedUntouched(
        client.call<{ complete: boolean }>('onboarding.status', {}),
        document,
        () => {
          // …unless they moved on meanwhile (it can answer late): a place they opened, a document's link.
          if (!DOC_LINK.test(location.hash))
            setView((v) => lateMove(v, landing.current === 'home' ? 'home' : first, 'company', false));
        },
      );
    // Past a required two-factor's deadline, only setup is open (decision 20, QA-093).
    if (s)
      void twoFactorState(true).then((t) => {
        setBlocked(t?.blocked ?? false);
        if (t?.blocked) setView('signin');
      });
  };

  const held = session?.permissions;
  const openDoc = useCallback(
    async (d: DocRef) => {
      if (!held) return;
      docView.current = 'pending';
      const t = await locateDoc(d, (a, i) => client.call(a, i), {
        place: (v) => PLACES.some((p) => p.view === v && canSee(p, held)),
        payroll: seesPayroll(held),
      });
      if ('error' in t) {
        docView.current = null;
        setDocSaid(t.error);
        history.replaceState(null, '', `${location.pathname}${location.search}#/${viewRef.current}`);
        return;
      }
      const at = Date.now();
      if (t.view === 'customers') setCustomerOpen({ customerId: t.customerId, focus: t.focus, at });
      // a finalized invoice opens its sheet over its customer's Money tab
      if (t.view === 'customers' && d.kind === 'invoice') openInvoice(d.id);
      // a draft invoice's link: its job's page, over Review (the address becomes the job's)
      if (t.view === 'review') {
        history.replaceState(null, '', `${location.pathname}${location.search}${jobHref(t.jobId)}`);
        setJobPage(t.jobId);
        docView.current = null;
        return void setView('review');
      }
      if (t.view === 'books') setBooksPart({ part: t.part, focus: t.focus, at });
      docView.current = t.view;
      setView(t.view);
    },
    [held],
  );
  // A document link in the address when signing in (a reload, a link someone sent).
  useEffect(() => {
    const d = parseDocLink(location.hash);
    if (d) void openDoc(d);
  }, [openDoc]);
  useEffect(() => {
    const onHash = () => {
      if (held) followMoved();
      const d = parseDocLink(location.hash);
      if (d) return void openDoc(d);
      // a job's page opens over the place it's opened from; any other address (Close, Back) closes it
      const job = parseJobLink(location.hash);
      setJobPage(job && held ? job : null);
      if (job && held) {
        // reached by its address (a pasted link, not a place in the app): over the board, so the rail
        // and the crumb both say the board
        if (!openedInApp(job)) {
          const under = linkedPlace('#/board', held) ?? linkedPlace('#/customers', held);
          if (under) setView(under);
        }
        return;
      }
      if (location.hash === YOUR_SIGN_IN) setView('signin');
      const own = held && designRef.current === 'blueprint' ? blueprintLink(location.hash, held) : null;
      if (own) return void setView(own);
      const linked = held
        ? (linkedPlace(location.hash, held) ??
          // a part's own link (#/books/banking, back to it): its screen, on it
          partLink(location.hash, held) ??
          // a customer's link: Customers opens them (it reads the address), or already has them open
          (CUSTOMER_LINK.test(location.hash) ? linkedPlace('#/customers', held) : null))
        : null;
      if (linked) setView(linked);
      // A link to no place this person opens (#/nowhere) stays where they are, and says so (QA-361);
      // in Blueprint, a place with its own words for them (Hours) says them (QA-774).
      else if (
        held &&
        (PLACE_LINK.test(location.hash) || PART_LINK.test(location.hash) || CUSTOMER_LINK.test(location.hash))
      ) {
        const words = designRef.current === 'blueprint' ? notYoursWords(location.hash, held) : null;
        if (words) setDocSaid(words);
        history.replaceState(null, '', `${location.pathname}${location.search}#/${viewRef.current}`);
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [held, openDoc]);
  // The address follows the place. A one-time link (#billing, a ledger's return, Your sign-in's)
  // stays until its screen has read it; a document's link stays while its place is open, and a
  // customer's while Customers is.
  useEffect(() => {
    if (!session) return;
    if (DOC_LINK.test(location.hash)) {
      if (docView.current === 'pending' || docView.current === view) return;
      docView.current = null;
    } else if (CUSTOMER_LINK.test(location.hash)) {
      if (view === 'customers') return;
    } else if (JOB_LINK.test(location.hash)) {
      if (jobPage) return;
    } else if (location.hash && !PLACE_LINK.test(location.hash) && !PART_LINK.test(location.hash)) return;
    // a screen with parts keeps the open one in the address (#/books/banking)
    const part = view === 'books' ? shownPart.books : view === 'settings' ? shownPart.settings : undefined;
    const to = `#/${view}${part ? `/${part}` : ''}`;
    if (location.hash === to) return;
    // another part of the same screen is a step of its own, so back goes to the one before it
    const was = linkedPart(location.hash);
    const step = !!part && was?.view === view && was.part !== part;
    history[step ? 'pushState' : 'replaceState'](null, '', `${location.pathname}${location.search}${to}`);
  }, [session, view, shownPart, jobPage]);

  useEffect(() => {
    void client.session().then((s) => {
      if (s) setCsrf(s.csrf);
      allowLiveUpdates(s?.liveUpdates === true); // QA-397: only a role that sees every job
      setCompanyMode(s?.mode); // while it mirrors ServiceTitan, screens say what's ServiceTitan's
      setTrial(s?.trial); // a trial open: the notice says its changes are thrown away
      setMapsKey(s?.mapsKey, s?.mapsOff, mayTurnOnMaps(s)); // the web map’s key, or why not (BL-018, QA-756)
      begin(
        s
          ? {
              tenant: s.tenant,
              roles: s.principal.roles ?? [],
              permissions: s.principal.permissions ?? [],
              staffId: s.principal.staffId ?? null,
              ...(s.principal.membershipId ? { who: s.principal.membershipId } : {}),
              ...(s.companyName ? { companyName: s.companyName } : {}),
            }
          : null,
      );
    });
  }, []);

  // The design the page draws in, always the one its theme (#flow-theme) is for: they change together
  // (showTheme), because one design's tokens under the other's stylesheet are unreadable (the sign-in
  // page drew Blueprint's tokens on Riverstone's sheet, 24 Oct). Until a theme arrives, the page's own
  // (index.html: Blueprint's, with its data-design).
  const [design, setDesign] = useState<Design>(() => designOf(document.documentElement.dataset.design));
  const designRef = useRef(design);
  designRef.current = design;
  const showTheme = useCallback((css: string, tokenSet: string | null | undefined): Design => {
    themeStyle().textContent = css;
    const d = designOf(tokenSet ?? undefined);
    // Blueprint's bridge and frame styles key off <html data-design> (blueprint/blueprint.css).
    document.documentElement.dataset.design = d;
    setDesign(d);
    return d;
  }, []);

  // On a company's own host, the sign-in page says whose it is (GET /brand, before anyone signs in):
  // its name and initials stone, and its colour (only what its customers already see), in its design.
  const [brand, setBrand] = useState<{ name: string } | null>(null);
  useEffect(() => {
    if (session !== null || /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) return;
    client
      .brand()
      .then((b) => {
        setBrand({ name: b.name });
        if (b.css) showTheme(b.css, b.tokenSet);
      })
      .catch(() => setBrand(null));
  }, [session, showTheme]);

  // The company's theme: its design language's tokens (design.language) with its own colour,
  // generated (and contrast-checked) by the server.
  useEffect(() => {
    if (!session) return;
    void client
      .call<{ css: string; tokenSet?: string }>('branding.theme', { audience: 'staff' })
      .then((t) => {
        const d = showTheme(t.css, t.tokenSet);
        // touched since sign-in (a place opened, even the one they landed on): they stay where they are
        const touched = landingTouch.current?.touched ?? true;
        landingTouch.current?.stop();
        landingTouch.current = null;
        if (d === 'blueprint' && session.roles.includes('owner') && landing.current && !touched) {
          const from = landing.current;
          landing.current = 'home';
          setView((v) => lateMove(v, from, 'home', false));
        }
        if (d === 'riverstone') setView((v) => (blueprintOnly(v) ? firstPlaceFor(session.permissions) : v));
        if (d === 'blueprint' && notYours.current) setDocSaid(notYours.current);
        notYours.current = null;
      })
      .catch(() => undefined);
    // Times are the company's, whatever the browser's zone.
    void client
      .call<{ timeZone: string }>('dispatch.board', { date: new Date().toISOString().slice(0, 10) })
      .then((b) => setTimeZone(b.timeZone))
      .catch(() => undefined);
  }, [session, showTheme]);

  // What the person holds, read again when it may have changed with a page open (QA-682): the live
  // stream says the owner took a permission back or changed roles, or a call was refused (a burst of
  // them is one read). The nav and the page follow at once, with no reload.
  const accessTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const refreshAccess = useCallback(() => {
    clearTimeout(accessTimer.current);
    accessTimer.current = setTimeout(() => {
      void client
        .session()
        .then((s) => {
          if (!s) return;
          const roles = s.principal.roles ?? [];
          const permissions = s.principal.permissions ?? [];
          setSession((cur) =>
            !cur || (sameList(cur.permissions, permissions) && sameList(cur.roles, roles))
              ? cur
              : { ...cur, roles, permissions },
          );
        })
        .catch(() => undefined);
    }, 250);
  }, []);
  useEffect(() => onForbidden(refreshAccess), [refreshAccess]);
  // A page that what they hold opened before and doesn't now closes, and says why.
  const heldBefore = useRef<readonly string[] | null>(null);
  useEffect(() => {
    const now = session?.permissions ?? null;
    const before = heldBefore.current;
    heldBefore.current = now;
    if (!now || !before || before === now) return;
    const v = viewRef.current;
    const opensWith = (h: readonly string[]) =>
      PLACES.some((p) => p.view === v && canSee(p, h)) || (blueprintOnly(v) && canOpenIn(h)(v));
    if (opensWith(before) && !opensWith(now)) {
      setDocSaid(ACCESS_CLOSED);
      setView(firstPlaceFor(now));
    }
  }, [session?.permissions]);

  // A ringing call pops on Calls and, anywhere else, rings in the shell's banner (QA-726), and counts
  // on the nav wherever you are; a new or read notification re-reads the bell's list (debounced: a
  // batch of events is one read). The shell's stream is the one the sheets share (onLiveChange).
  useEffect(() => {
    if (!session) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let jobTimer: ReturnType<typeof setTimeout> | undefined;
    const stop = onLiveChange(({ type, entityId }) => {
      // the call ringing in the banner answered, ended or changed: the banner reads it again (before
      // the Inbox's counts take the call's events below)
      if (
        type !== 'call.received' &&
        CALL_EVENTS.test(type) &&
        entityId &&
        entityId === ringingRef.current?.id
      )
        setRingingCall((r) => (r && r.id === entityId ? { id: r.id, tick: r.tick + 1 } : r));
      // the company entered (or left) the mirror: read its mode again
      if (MODE_EVENTS.test(type)) {
        void client.session().then((s) => {
          setCompanyMode(s?.mode);
          setTrial(s?.trial);
        });
        return;
      }
      // the owner changed what someone holds: read what this person holds again
      if (ACCESS_EVENTS.test(type)) {
        refreshAccess();
        return;
      }
      if (INBOX_EVENTS.test(type)) {
        clearTimeout(jobTimer);
        jobTimer = setTimeout(() => reloadInbox.current(), 250);
        return;
      }
      if (
        type === 'notification.created' ||
        type === 'notification.updated' ||
        type === 'notifications.read'
      ) {
        clearTimeout(timer);
        timer = setTimeout(() => void reloadNotes.current(), 250);
        return;
      }
      if (type !== 'call.received' || !entityId) return;
      // never in place of the call someone has open (QA-771): the strip or the banner has it then
      setCallId((open) => openAfterRing(open, entityId));
      // on Calls it pops in place; anywhere else the banner says who's calling (QA-726)
      if (viewRef.current !== 'calls') {
        setRinging((n) => n + 1);
        setRingingCall({ id: entityId, tick: 0 });
      }
    });
    return () => {
      clearTimeout(timer);
      clearTimeout(jobTimer);
      stop();
    };
  }, [session, refreshAccess]);

  if (session === undefined) return null;
  // before a session the sign-in page draws in the design of the theme on the page (Blueprint's on
  // catalogued parts, BL-189; Riverstone's Login.tsx)
  const SignIn: typeof Login = design === 'blueprint' ? SignInPage : Login;
  if (!session)
    return (
      <DesignContext.Provider value={design}>
        <SignIn
          {...(brand ? { brand } : {})}
          onSignedIn={(csrf, tenant, recoveryLeft) => {
            setCsrf(csrf);
            void client.session().then((s) => {
              allowLiveUpdates(s?.liveUpdates === true); // QA-397: only a role that sees every job
              setCompanyMode(s?.mode); // while it mirrors ServiceTitan, screens say what's ServiceTitan's
              setTrial(s?.trial); // a trial open: the notice says its changes are thrown away
              setMapsKey(s?.mapsKey, s?.mapsOff, mayTurnOnMaps(s)); // the web map’s key, or why not (BL-018, QA-756)
              begin(
                s
                  ? {
                      tenant,
                      roles: s.principal.roles ?? [],
                      permissions: s.principal.permissions ?? [],
                      staffId: s.principal.staffId ?? null,
                      ...(s.principal.membershipId ? { who: s.principal.membershipId } : {}),
                      ...(s.companyName ? { companyName: s.companyName } : {}),
                    }
                  : null,
              );
              // Signed in with a recovery code: Your sign-in says how many are left (QA-093).
              if (s && recoveryLeft !== undefined) {
                setRecoveryLeft(recoveryLeft);
                setView('signin');
              }
            });
          }}
        />
      </DesignContext.Provider>
    );

  const nav: NavProps = {
    tenant: session.tenant,
    ...(session.companyName ? { companyName: session.companyName } : {}),
    timeZone, // the header clock keeps the company's time, not the browser's
    view,
    part:
      view === 'books'
        ? (shownPart.books ?? null)
        : view === 'settings'
          ? (shownPart.settings ?? null)
          : null,
    ringing,
    waiting: inbox.counts,
    roles: session.roles,
    permissions: session.permissions,
    staffId: session.staffId,
    onView: (v, part) => {
      if (part) askPart(v, part);
      // Leaving Your sign-in, its link is spent: following it again opens it again.
      if (v !== 'signin' && location.hash === YOUR_SIGN_IN)
        history.replaceState(null, '', location.pathname + location.search);
      if (v === 'calls') {
        setRinging(0);
        setRingingCall(null); // Calls pops it in place: the banner never shows it twice
      }
      if (v === 'book') setBookStart(null);
      if (v === 'messages') setMessagesStart(null);
      setJobPage(null);
      setView(v);
    },
    onSignOut: async () => {
      await client.signOut();
      setRingingCall(null);
      // The next person to sign in here starts at their own first place.
      history.replaceState(null, '', location.pathname + location.search);
      setCsrf(null);
      setMapsKey(null);
      setBlocked(false);
      setSession(null);
    },
    notes,
    // Opening a notification goes to the place that deals with it, if this person may open it.
    onOpenNote: (n: Note) => {
      const to = sourceOf(n);
      if (!to || !PLACES.some((p) => p.view === to.view && canSee(p, session.permissions))) return;
      if (to.part) setBooksPart({ part: to.part, at: Date.now() });
      if (to.agentsPart) setAgentsPart({ part: to.agentsPart, at: Date.now() });
      if (to.membershipsPart)
        setMembershipsPart({
          part: to.membershipsPart,
          at: Date.now(),
          ...(to.focus ? { focus: to.focus } : {}),
        });
      setView(to.view);
      // a tech's message, or a job ready for review: the job's page over the place (BL-028)
      if (to.job) openJob(to.job);
    },
  };
  // Where a ui-web view opens a place (blueprint/Home.tsx): only a place what this person holds opens.
  const may = (v: View) => PLACES.some((p) => p.view === v && canSee(p, session.permissions));
  const opens = (t: OpenTarget): boolean =>
    t.kind === 'approvals'
      ? may('agents')
      : t.kind === 'numbers'
        ? may('huddle')
        : t.kind === 'owed'
          ? may('books')
          : t.kind === 'customer'
            ? may('customers')
            : t.kind === 'book'
              ? may('book')
              : t.kind === 'job';
  const openTarget = (t: OpenTarget) => {
    if (!opens(t)) return;
    const at = Date.now();
    if (t.kind === 'approvals') {
      setAgentsPart({ part: 'approvals', at });
      setView('agents');
    } else if (t.kind === 'numbers') setView('huddle');
    else if (t.kind === 'owed') {
      setBooksPart({ part: 'receivables', at });
      setView('books');
    } else if (t.kind === 'customer') location.hash = customerHref(t.id);
    else if (t.kind === 'book') {
      setBookStart(t.customerId ? { customerId: t.customerId } : null);
      setView('book');
    } else if (t.kind === 'job') openJob(t.id);
  };
  // In Blueprint every screen sits in the frame: the places, the rail, the sheet (blueprint/Frame.tsx).
  // Blueprint's calls strip under the top bar (calls round): for those who see calls, on every page
  const callsStrip = session.permissions.includes('calls.view') ? (
    <CallsStripHost
      openId={view === 'calls' ? callId : null}
      onCalls={view === 'calls' && !jobPage}
      onOpen={(id) => {
        setRingingCall(null);
        setRinging(0);
        setCallId(id);
        setJobPage(null);
        setView('calls');
      }}
    />
  ) : undefined;
  const framed = (el: ReactNode) =>
    design === 'blueprint' ? (
      <DesignContext.Provider value="blueprint">
        <Frame nav={nav} strip={callsStrip}>
          {el}
        </Frame>
      </DesignContext.Provider>
    ) : (
      el
    );
  // Your sign-in is everyone's, the office's and the technicians' (QA-093, QA-092).
  // In Blueprint it's a Setup page on the SettingsPage pattern (BL-189); Riverstone keeps its screen.
  if (view === 'signin') {
    const signIn = {
      nav,
      timeZone,
      ...(recoveryLeft !== null ? { usedRecovery: recoveryLeft } : {}),
      onState: onTwoFactor,
    };
    return framed(design === 'blueprint' ? <YourSignInPage {...signIn} /> : <SignInScreen {...signIn} />);
  }
  if (blocked) return framed(<TwoFactorHold nav={nav} />);

  // The office app is for the office: a technician is sent to the tech app (QA-257). In Blueprint their
  // web Home is their own scorecard (BL-123), and the rest stays the tech app's. Who is the office's is
  // what they hold, whichever role gives it, a company's own role included (QA-680), never role names.
  const office = opensOffice(session.permissions, design === 'blueprint');
  if (!office && design === 'blueprint' && ownScorecardOnly(session.permissions))
    return framed(<PerformanceScreen nav={{ ...nav, view: 'performance' }} held={session.permissions} />);
  // the tech app's own work (appointments.resume): a technician, or a company's own role made from one
  const fieldWork = session.permissions.includes('appointments.resume');
  if (!office)
    return (
      <main className="login">
        <section className="sheet login-sheet" aria-labelledby="not-office">
          <h1 id="not-office">This is the office app</h1>
          <p>
            {fieldWork
              ? 'Technicians use the Flowology tech app on their iPad or phone.'
              : 'Your role has no office screens.'}
          </p>
          {fieldWork && (
            <a className="btn primary wide" href="/tech/">
              Open the tech app
            </a>
          )}
          <button className="btn soft wide" onClick={() => setView('signin')}>
            Your sign-in and pairing an iPad
          </button>
          <button
            className="btn soft wide"
            onClick={() =>
              void client.signOut().then(() => {
                setCsrf(null);
                setMapsKey(null);
                setSession(null);
              })
            }
          >
            Sign out
          </button>
        </section>
      </main>
    );

  const screen = () => {
    // Under a job's page only the board stays mounted (hidden), so Close finds it as it was; any other
    // place draws again from its address when the page closes.
    if (jobPage && view !== 'board') return null;
    if (view === 'calls') {
      // Blueprint's Inbox › Calls (calls/CallsPage.tsx) and Riverstone's (Calls.tsx): one screen's state
      const Calls = design === 'blueprint' ? CallsPage : CallsScreen;
      return (
        <Calls
          nav={nav}
          callId={callId}
          onOpenCall={(id) => {
            setCallId(id);
            setRinging(0);
          }}
          onBoard={() => setView('board')}
        />
      );
    }
    // A place what the person holds doesn't open falls through to the board.
    const open = (v: View) => PLACES.some((p) => p.view === v && canSee(p, session.permissions));
    // Blueprint's own pages, for whoever may open them (blueprint/places.ts)
    const own = design === 'blueprint' && blueprintOnly(view) && canOpenIn(session.permissions)(view);
    if (own && view === 'home')
      return <HomeScreen nav={nav} held={session.permissions} onOpen={openTarget} opens={opens} />;
    if (own && view === 'marketing') return <AdsScreen nav={nav} onOpen={openTarget} />;
    if (own && view === 'adcampaigns') return <AdCampaignsScreen nav={nav} onOpen={openTarget} />;
    if (own && view === 'adleads') return <AdLeadsScreen nav={nav} onOpen={openTarget} />;
    if (own && view === 'operations') return <ProfitScreen nav={nav} onOpen={openTarget} />;
    if (own && view === 'profitjobs') return <ProfitJobsScreen nav={nav} onOpen={openTarget} />;
    if (own && view === 'performance')
      return <PerformanceScreen nav={nav} held={session.permissions} onOpen={openTarget} />;
    if (own && view === 'map')
      return (
        <MapScreen
          nav={nav}
          timeZone={timeZone}
          onBoard={(date, visit) => {
            setBoardDay({ date, at: Date.now(), ...(visit ? { visit } : {}) });
            setView('board');
          }}
        />
      );
    if (own && view === 'team')
      return <TeamScreen nav={nav} timeZone={timeZone} onBoard={() => setView('board')} />;
    if (own && view === 'timeoff') return <TimeOffScreen nav={nav} timeZone={timeZone} />;
    // Setup's own pages for two parts of other screens: Company's plan and data, Your sign-in's AI tools
    if (own && view === 'yourplan') return <YourPlanPage nav={nav} />;
    if (own && view === 'aitools') return <AiToolsPage nav={nav} timeZone={timeZone} />;
    // Inbox's own pages: one request to decide, and the bell's list (inbox/InboxPages.tsx)
    if (own && view === 'approval')
      return (
        <ApprovalScreen
          nav={nav}
          timeZone={timeZone}
          onAllRequests={() => {
            setAgentsPart({ part: 'approvals', at: Date.now() });
            setView('agents');
          }}
        />
      );
    if (own && view === 'notifications')
      return <NotificationsScreen nav={nav} timeZone={timeZone} onApprovals={() => setView('approval')} />;
    // Customers' Equipment, a row opening its customer's record on it; Stock's Catalog and prices
    if (own && view === 'equipment')
      return (
        <EquipmentScreen
          nav={nav}
          timeZone={timeZone}
          onOpen={(e) => {
            setCustomerOpen({ customerId: e.customer.id, equipmentId: e.id, at: Date.now() });
            setView('customers');
          }}
        />
      );
    if (own && view === 'catalog') return <CatalogScreen nav={nav} />;
    if (view === 'books' && open('books'))
      return (
        <BooksScreen
          nav={nav}
          held={session.permissions}
          // Blueprint has one timesheet: Payroll's opens Hours on the period (Riverstone keeps its own)
          {...(design === 'blueprint' && open('hours')
            ? {
                onHours: (date: string) => {
                  setHoursOpen({ date, at: Date.now() });
                  setView('hours');
                },
              }
            : {})}
          {...(booksPart ? { open: booksPart } : {})}
          onPart={(p) => setShownPart((o) => (o.books === p ? o : { ...o, books: p }))}
        />
      );
    if (view === 'review' && open('review')) return <ReviewScreen nav={nav} timeZone={timeZone} />;
    if (view === 'hours' && open('hours'))
      return design === 'blueprint' ? (
        // Blueprint's Hours: the team's pay period, a person's Timesheet, the quick edit (timesheet rules §3–§5)
        <HoursRoute
          nav={nav}
          timeZone={timeZone}
          {...(hoursOpen ? { open: hoursOpen } : {})}
          onBoard={() => setView('board')}
          {...(open('books')
            ? {
                onPayroll: () => {
                  setBooksPart({ part: 'payroll', at: Date.now() });
                  setView('books');
                },
              }
            : {})}
        />
      ) : (
        <HoursScreen
          nav={nav}
          timeZone={timeZone}
          {...(hoursOpen?.staffId ? { open: { ...hoursOpen, staffId: hoursOpen.staffId } } : {})}
        />
      );
    if (view === 'messages' && open('messages'))
      return (
        <MessagesScreen
          nav={nav}
          timeZone={timeZone}
          canSend={has(['messages.send'])}
          start={messagesStart}
        />
      );
    if (view === 'lists' && open('lists')) return <ListsScreen nav={nav} />;
    if (view === 'tasks' && open('tasks'))
      return (
        <TasksScreen
          nav={nav}
          onChanged={() => inbox.reload()}
          onOpenReview={(jobId) => openJob(jobId)}
          onOpenApprovals={() => {
            setAgentsPart({ part: 'approvals', at: Date.now() });
            setView('agents');
          }}
        />
      );
    if (view === 'agents' && open('agents'))
      return <AgentsScreen nav={nav} timeZone={timeZone} {...(agentsPart ? { open: agentsPart } : {})} />;
    if (view === 'company' && open('company')) return <CompanyScreen nav={nav} />;
    if (view === 'migration' && open('migration'))
      return <MigrationScreen nav={nav} held={session.permissions} />;
    if (view === 'huddle' && open('huddle')) return <HuddleScreen nav={nav} held={session.permissions} />;
    // Blueprint's Reports is its own page on catalogued parts (BL-189); Riverstone keeps Reports.tsx
    if (view === 'reports' && open('reports'))
      return design === 'blueprint' ? (
        <ReportsPage nav={nav} held={session.permissions} />
      ) : (
        <ReportsScreen nav={nav} held={session.permissions} />
      );
    if (view === 'memberships' && open('memberships'))
      return (
        <MembershipsScreen
          nav={nav}
          held={session.permissions}
          {...(membershipsPart ? { open: membershipsPart } : {})}
        />
      );
    if (view === 'inventory' && open('inventory'))
      return (
        <InventoryScreen nav={nav} held={session.permissions} {...(stockOpen ? { open: stockOpen } : {})} />
      );
    if (view === 'settings' && open('settings'))
      return (
        <SettingsScreen
          nav={nav}
          timeZone={timeZone}
          {...(settingsPart ? { open: settingsPart } : {})}
          onPart={(p) => setShownPart((o) => (o.settings === p ? o : { ...o, settings: p }))}
        />
      );
    if (view === 'tobook' && open('tobook')) {
      const bookFrom = {
        onBook: (e: SoldEstimate) => {
          setBookStart({
            customerId: e.customerId,
            estimate: { id: e.estimateId, number: e.number, jobId: e.jobId },
          });
          setView('book');
        },
        onBookLead: (l: Lead) => {
          setBookStart({
            ...(l.customerId ? { customerId: l.customerId } : {}),
            lead: { id: l.id, name: l.name, phone: l.phone, email: l.email, summary: l.summary },
          });
          setView('book');
        },
        onChanged: () => reloadInbox.current(),
      };
      // Blueprint's To schedule is its own page on ListPage (BL-189); Riverstone keeps To book
      return design === 'blueprint' ? (
        <ToSchedulePage nav={nav} timeZone={timeZone} {...bookFrom} />
      ) : (
        <ToBookScreen nav={nav} timeZone={timeZone} {...bookFrom} />
      );
    }
    if (view === 'customers' && open('customers'))
      return (
        <CustomersScreen
          nav={nav}
          timeZone={timeZone}
          {...(customerOpen ? { open: customerOpen } : {})}
          onBook={(customerId) => {
            setBookStart({ customerId });
            setView('book');
          }}
          {...(open('messages')
            ? {
                onOpenMessages: (customer: { id: string; name: string }) => {
                  setMessagesStart({ customer });
                  setView('messages');
                },
              }
            : {})}
        />
      );
    if (view === 'book') {
      // from a booking: the board on its day, the visit open and scrolled to (QA-768)
      const toBoard = (at?: { date: string; visit: string }) => {
        if (at) setBoardDay({ date: at.date, at: Date.now(), visit: at.visit });
        setView('board');
      };
      // Blueprint's Book a visit is its own page on catalogued parts (BL-189); Riverstone keeps Book a job
      return design === 'blueprint' ? (
        <BookPage nav={nav} start={bookStart} timeZone={timeZone} onBoard={toBoard} />
      ) : (
        <BookScreen nav={nav} start={bookStart} timeZone={timeZone} onBoard={toBoard} />
      );
    }
    return (
      <BoardScreen
        nav={nav}
        {...(session.who ? { who: session.who } : {})}
        {...(boardDay ? { openDay: boardDay } : {})}
        canDispatch={session.permissions.includes('jobs.dispatch')}
        hidden={!!jobPage}
        {...(open('hours')
          ? {
              onOpenHours: (staffId: string, date?: string) => {
                setHoursOpen({ staffId, ...(date ? { date } : {}), at: Date.now() });
                setView('hours');
              },
            }
          : {})}
        // the tech menu's Truck stock opens Stock at their truck alone
        {...(open('inventory')
          ? {
              onOpenTruck: (locationId: string) => {
                setStockOpen({ locationId, at: Date.now() });
                setView('inventory');
              },
            }
          : {})}
      />
    );
  };
  // what the job's page hands on: book an estimate's work, its messages, a day on the board; each
  // leaves the page for that place, whose own address then replaces the job's
  const leaveFor = (v: View) => {
    setJobPage(null);
    setView(v);
  };
  const bookEstimate = (e: { customerId: string; id: string; number: string; jobId: string }) => {
    setBookStart({ customerId: e.customerId, estimate: { id: e.id, number: e.number, jobId: e.jobId } });
    leaveFor('book');
  };
  const openMessages = (job: { id: string; number: string }) => {
    setMessagesStart({ job });
    leaveFor('messages');
  };
  // the page is over the place it was opened from, which its crumb names; reached by a link, it is
  // over the board (or Customers), and its crumb and Close go to the board
  const page = jobPage && (
    <JobRoute
      key={jobPage}
      jobId={jobPage}
      // opened from the board's visit panel: the page opens Visits on that visit
      fromVisit={visitOpenedFrom(jobPage)}
      held={session.permissions}
      timeZone={timeZone}
      // the crumb names the place the rail shows: the one it was opened from, or the board
      crumb={{ label: pageLabel(view), onPress: () => leaveJobPage(jobPage, window, `#/${view}`) }}
      onClose={() => leaveJobPage(jobPage, window, `#/${view}`)}
      onBookEstimate={bookEstimate}
      onOpenMessages={openMessages}
      onOpenCustomer={(customerId) => {
        location.hash = customerHref(customerId);
      }}
      onOpenInvoice={(invoiceId) => openInvoice(invoiceId)}
      onBoard={(date, visit) => {
        setBoardDay({ date, at: Date.now(), ...(visit ? { visit } : {}) });
        leaveFor('board');
      }}
      onChanged={() => reloadInbox.current()}
    />
  );

  const again = lost && (
    <DesignContext.Provider value={design}>
      <SignIn
        again
        tenant={session.tenant}
        onSignedIn={(csrf) => {
          setCsrf(csrf);
          setLost(false);
        }}
      />
    </DesignContext.Provider>
  );
  return (
    <>
      {framed(
        <>
          {screen()}
          {page}
        </>,
      )}
      {/* the sheets open over every place, so they draw in its design too */}
      <DesignContext.Provider value={design}>
        <InvoiceSheetHost
          held={session.permissions}
          timeZone={timeZone}
          onOpenCustomer={(customerId, invoiceId) => {
            setCustomerOpen({ customerId, focus: invoiceId, at: Date.now() });
            setView('customers');
          }}
        />
        <SellMembershipHost today={todayIn(timeZone)} onSold={() => reloadInbox.current()} />
        {/* a call ringing anywhere but Calls (QA-726): who it is, and the way to their panel. In
            Blueprint the calls strip under the top bar says so instead (CallsStripHost) */}
        {ringingCall &&
          view !== 'calls' &&
          design !== 'blueprint' &&
          session.permissions.includes('calls.view') && (
            <RingingBanner
              callId={ringingCall.id}
              tick={ringingCall.tick}
              onOpen={(id) => {
                setRingingCall(null);
                setRinging(0);
                setCallId(id);
                setJobPage(null);
                setView('calls');
              }}
              onGone={() => setRingingCall((r) => (r?.id === ringingCall.id ? null : r))}
            />
          )}
      </DesignContext.Provider>
      {again}
      {docSaid && (
        <p className="toast" role="status">
          {docSaid}
        </p>
      )}
      <ConnectionLine />
    </>
  );
}
